import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  type Chart as ChartType,
  type ChartDataset,
  type Plugin,
} from "chart.js";
import classNames from "classnames";
import * as React from "react";

import {
  FALLBACK_COLOR,
  applyAxisColors,
  computedColorOf,
  useColorSchemeVersion,
} from "@/shared/charts";
import { type ClockPreference, useDateTimeFormat } from "@/shared/datetime";
import {
  type ActivityPeriod,
  type FilledActivityBucket,
  InvocationStatus,
  formatBucketStart,
  nextBucketStart,
  spansMultipleDays,
  type TimeRange,
} from "@/shared/domain/actions";

import { type ActivityChartMode } from "./activityChartModes";
import styles from "./InvocationActivity.module.css";

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
);

type CrosshairPlugin = Plugin<"bar"> & { color: string };

/**
 * A vertical hairline at the hovered bucket, so a reader aims at a time rather than at a 2px line.
 *
 * One instance per chart, handed to that chart in its own `plugins` rather than registered:
 * Chart.js runs every registered plugin on every chart in the app, so registering this one would
 * draw the hairline on unrelated charts unless each of them opted out by id.
 *
 * `color` is filled in by {@link applySchemeColors}. Resolving the CSS variable in the draw hook
 * instead would force a style recalculation on every draw, and hovering redraws once per bucket
 * the pointer crosses.
 */
function createCrosshair(): CrosshairPlugin {
  const crosshair: CrosshairPlugin = {
    id: "invocationActivityCrosshair",
    color: FALLBACK_COLOR,
    afterDatasetsDraw(chart) {
      const active = chart.getActiveElements();
      if (active.length === 0) {
        return;
      }

      const { ctx, chartArea } = chart;
      const x = active[0].element.x;

      ctx.save();
      ctx.strokeStyle = crosshair.color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
      ctx.restore();
    },
  };
  return crosshair;
}

export type { TimeRange };

interface SeriesSpec {
  key: string;
  label: string;
  /** A CSS custom property; resolved against the canvas at draw time. */
  color: string;
  kind: "line" | "bar";
  dashed?: boolean;
  /** Internal statuses this series stands for; empty means it is not a status at all. */
  statuses: InvocationStatus[];
  value: (bucket: FilledActivityBucket, limit: number | null) => number | null;
}

const SLOT_HOLDING = [
  InvocationStatus.Scheduled,
  InvocationStatus.Downloading,
  InvocationStatus.Processing,
  InvocationStatus.Uploading,
];

const LIMIT_SERIES: SeriesSpec = {
  key: "limit",
  label: "Concurrency limit",
  color: "--gray-8",
  kind: "line",
  dashed: true,
  statuses: [],
  value: (_bucket, limit) => limit,
};

function peakOf(status: InvocationStatus) {
  return (bucket: FilledActivityBucket) =>
    bucket.peakInStatus[InvocationStatus[status]] ?? 0;
}

function enteredSum(...statuses: InvocationStatus[]) {
  return (bucket: FilledActivityBucket) =>
    statuses.reduce(
      (total, status) =>
        total + (bucket.enteredStatus[InvocationStatus[status]] ?? 0),
      0,
    );
}

/**
 * Active work is blue and passive work is gray, so running is blue and waiting is gray. The four
 * active stages overlap as lines, and overlapping lines need hues a reader can tell apart on
 * their own (a ramp of one hue only works where neighbours sit side by side), so each stage gets
 * a distinct hue: the set was checked with the dataviz palette validator, all pairs, for normal
 * and colour-deficient vision. Green and red stay reserved for outcomes, which stack with cancelled
 * between them so the two are never adjacent.
 */
function seriesFor(mode: ActivityChartMode): SeriesSpec[] {
  switch (mode) {
    case "rollup":
      return [
        LIMIT_SERIES,
        {
          key: "running",
          label: "Running",
          color: "--blue-9",
          kind: "line",
          statuses: SLOT_HOLDING,
          value: (bucket) => bucket.peakConcurrency,
        },
        {
          key: "waiting",
          label: "Waiting",
          color: "--gray-9",
          kind: "line",
          statuses: [InvocationStatus.Queued],
          value: peakOf(InvocationStatus.Queued),
        },
      ];
    case "inflight":
      return [
        LIMIT_SERIES,
        {
          key: "pending",
          label: "Pending",
          color: "--gray-9",
          kind: "line",
          statuses: [InvocationStatus.Queued],
          value: peakOf(InvocationStatus.Queued),
        },
        {
          key: "provisioning",
          label: "Provisioning",
          // Not `--orange-9`: it measures 2.79:1 against the light surface, under the 3:1 floor
          // `seriesColors.css` sets. `--yellow-11` is the only ramp step that clears the floor in
          // both schemes (4.33 light, 14.15 dark) without colliding with the other stage hues.
          color: "--yellow-11",
          kind: "line",
          statuses: [InvocationStatus.Scheduled],
          value: peakOf(InvocationStatus.Scheduled),
        },
        {
          key: "downloading",
          label: "Downloading",
          color: "--chart-purple",
          kind: "line",
          statuses: [InvocationStatus.Downloading],
          value: peakOf(InvocationStatus.Downloading),
        },
        {
          key: "processing",
          label: "Processing",
          color: "--blue-9",
          kind: "line",
          statuses: [InvocationStatus.Processing],
          value: peakOf(InvocationStatus.Processing),
        },
        {
          key: "uploading",
          label: "Uploading",
          color: "--magenta-11",
          kind: "line",
          statuses: [InvocationStatus.Uploading],
          value: peakOf(InvocationStatus.Uploading),
        },
      ];
    case "outcomes":
      return [
        {
          key: "completed",
          label: "Completed",
          color: "--green-9",
          kind: "bar",
          statuses: [InvocationStatus.Completed],
          value: enteredSum(InvocationStatus.Completed),
        },
        {
          key: "cancelled",
          label: "Cancelled",
          color: "--yellow-11",
          kind: "bar",
          statuses: [InvocationStatus.Cancelled],
          value: enteredSum(InvocationStatus.Cancelled),
        },
        {
          key: "failed",
          label: "Failed",
          color: "--red-9",
          kind: "bar",
          statuses: [InvocationStatus.Failed, InvocationStatus.Deadly],
          value: enteredSum(InvocationStatus.Failed, InvocationStatus.Deadly),
        },
      ];
    case "credits":
      return [
        {
          key: "credits",
          label: "Processing units (RPU)",
          color: "--blue-9",
          kind: "bar",
          statuses: [],
          value: (bucket) => bucket.computeCreditMillionths / 1_000_000,
        },
      ];
  }
}

export interface InvocationActivityChartProps {
  buckets: FilledActivityBucket[];
  period: ActivityPeriod;
  mode: ActivityChartMode;
  /** Drawn as a reference line, so "running is pinned at the cap" is readable. Null when uncapped. */
  concurrencyLimit: number | null;
  /** Internal statuses the page is filtered to; series for other statuses are not drawn. Empty means all. */
  statusFilter: InvocationStatus[];
  /** Enables drag-to-select across the x axis; called with the buckets' span on release. */
  onSelectRange?: (range: TimeRange) => void;
}

const MIN_DRAG_PX = 6;

/**
 * Invocation activity over time, in one of four readings of the same buckets.
 *
 * Series are not stacked in the line modes: stacking puts the waiting series on a moving baseline,
 * which hides the one trend worth reading.
 */
export function InvocationActivityChart({
  buckets,
  period,
  mode,
  concurrencyLimit,
  statusFilter,
  onSelectRange,
}: InvocationActivityChartProps) {
  const chartRef = React.useRef<ChartType | null>(null);
  const crosshairRef = React.useRef<CrosshairPlugin | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  // Which series are drawn. Local to the chart: it is a way of looking at this data, not a
  // property of it, and it survives a refetch.
  const [hidden, setHidden] = React.useState<Record<string, boolean>>({});
  // The pointer handlers read the ref: pointer events can arrive faster than React re-renders, so
  // a closure over state would see the position from an earlier render. The state only draws.
  const dragRef = React.useRef<{ from: number; to: number } | null>(null);
  const [drag, setDrag] = React.useState<{ from: number; to: number } | null>(
    null,
  );
  const updateDrag = (next: { from: number; to: number } | null) => {
    dragRef.current = next;
    setDrag(next);
  };

  const colorSchemeVersion = useColorSchemeVersion();
  // The axis labels' clock follows the reader's date/time preference.
  const { clock } = useDateTimeFormat().preference;

  const series = React.useMemo(
    () =>
      seriesFor(mode).filter(
        (spec) =>
          (spec.key !== "limit" || concurrencyLimit !== null) &&
          (statusFilter.length === 0 ||
            spec.statuses.length === 0 ||
            spec.statuses.some((status) => statusFilter.includes(status))),
      ),
    [mode, concurrencyLimit, statusFilter],
  );
  const drawn = React.useMemo(
    () => series.filter((spec) => !hidden[spec.key]),
    [series, hidden],
  );

  // `setCanvas` must not depend on the data, or every refetch would rebuild the chart.
  const dataRef = React.useRef({
    buckets,
    period,
    mode,
    concurrencyLimit,
    drawn,
    clock,
  });
  dataRef.current = { buckets, period, mode, concurrencyLimit, drawn, clock };

  const setCanvas = React.useCallback((canvas: HTMLCanvasElement | null) => {
    chartRef.current?.destroy();
    chartRef.current = null;
    if (!canvas) {
      return;
    }

    const stacked = dataRef.current.mode === "outcomes";
    const isCredits = dataRef.current.mode === "credits";

    const crosshair = createCrosshair();
    crosshairRef.current = crosshair;

    const chart = new Chart(canvas, {
      type: "bar",
      plugins: [crosshair],
      data: buildData(canvas, dataRef.current),
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        // One tooltip lists every series at that x, so the pointer never has to find a line.
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            stacked,
            grid: { display: false },
            ticks: {
              maxTicksLimit: 7,
              autoSkip: true,
              maxRotation: 0,
            },
          },
          y: {
            stacked,
            beginAtZero: true,
            border: { display: false },
            ticks: {
              precision: isCredits ? 2 : 0,
              maxTicksLimit: 5,
              callback: (value) => Number(value).toLocaleString(),
            },
          },
        },
        plugins: {
          tooltip: {
            boxWidth: 10,
            boxHeight: 10,
            boxPadding: 4,
            // Values lead, series names follow: the reader has the series and wants the number.
            callbacks: {
              label: (item) =>
                `${formatValue(item.parsed.y, isCredits)}  ${item.dataset.label ?? ""}`,
            },
          },
        },
      },
    });
    applySchemeColors(chart, crosshair);
    chart.update();
    chartRef.current = chart;
  }, []);

  React.useEffect(() => {
    const chart = chartRef.current;
    const crosshair = crosshairRef.current;
    if (!chart?.canvas || !crosshair) {
      return;
    }
    chart.data = buildData(chart.canvas, {
      buckets,
      period,
      concurrencyLimit,
      drawn,
      clock,
    });
    applySchemeColors(chart, crosshair);
    chart.update();
  }, [buckets, period, concurrencyLimit, drawn, clock, colorSchemeVersion]);

  const toggle = React.useCallback((key: string) => {
    setHidden((current) => ({ ...current, [key]: !current[key] }));
  }, []);

  // Clicking a name shows that series alone; clicking it again brings the others back.
  const solo = React.useCallback(
    (key: string) => {
      setHidden((current) => {
        const alreadyAlone = series.every(
          (spec) => spec.key === key || current[spec.key],
        );
        if (alreadyAlone) {
          return {};
        }
        return Object.fromEntries(
          series.map((spec) => [spec.key, spec.key !== key]),
        );
      });
    },
    [series],
  );

  const localX = (event: React.PointerEvent) => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    return rect ? event.clientX - rect.left : 0;
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!onSelectRange || event.button !== 0) {
      return;
    }
    const x = localX(event);
    updateDrag({ from: x, to: x });
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // A synthetic pointer (tests) has no id to capture; the drag still works while the
      // pointer stays over the chart.
    }
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const current = dragRef.current;
    if (current) {
      updateDrag({ from: current.from, to: localX(event) });
    }
  };

  const onPointerUp = () => {
    const current = dragRef.current;
    const chart = chartRef.current;
    if (current && chart && onSelectRange && buckets.length > 0) {
      const left = Math.min(current.from, current.to);
      const right = Math.max(current.from, current.to);
      if (right - left >= MIN_DRAG_PX) {
        onSelectRange(rangeBetweenPixels(chart, buckets, period, left, right));
      }
    }
    updateDrag(null);
  };

  return (
    <>
      <div
        ref={wrapperRef}
        className={classNames(styles.chart, {
          [styles.chartZoomable]: onSelectRange !== undefined,
        })}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => updateDrag(null)}
      >
        {/* Keyed on the mode: the stacked axes and value format are chart options, set at creation. */}
        <canvas
          key={mode}
          ref={setCanvas}
          role="img"
          aria-label={ariaLabelFor(mode)}
        />
        {drag && (
          <div
            className={styles.dragSelection}
            style={{
              left: Math.min(drag.from, drag.to),
              width: Math.abs(drag.to - drag.from),
            }}
          />
        )}
      </div>
      <ul className={styles.legend} aria-label="Chart series">
        {series.map((spec) => (
          <li key={spec.key} className={styles.legendItem}>
            <input
              type="checkbox"
              aria-label={spec.label}
              checked={!hidden[spec.key]}
              onChange={() => toggle(spec.key)}
              // The browser paints the check mark in whichever of black or white contrasts
              // with the accent, so the box can carry the series colour safely.
              style={{ accentColor: `var(${spec.color})` }}
            />
            {spec.dashed && (
              <span
                className={styles.legendKeyDashed}
                style={{ borderColor: `var(${spec.color})` }}
                aria-hidden="true"
              />
            )}
            <button
              type="button"
              className={styles.legendName}
              title={`Show only ${spec.label}`}
              onClick={() => solo(spec.key)}
            >
              {spec.label}
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * Applies the current scheme's colours to the axes, the grid and the crosshair. Called when the
 * chart is created and when the scheme changes, never per draw: resolving a CSS variable forces a
 * style recalculation.
 */
function applySchemeColors(chart: ChartType, crosshair: CrosshairPlugin): void {
  applyAxisColors(chart);
  crosshair.color = computedColorOf(chart.canvas, "--gray-8");
}

function ariaLabelFor(mode: ActivityChartMode): string {
  switch (mode) {
    case "rollup":
      return "Waiting and running invocations over time";
    case "inflight":
      return "Invocations in each stage over time";
    case "outcomes":
      return "Completed, failed and cancelled invocations over time";
    case "credits":
      return "Roboto processing units consumed over time";
  }
}

function formatValue(value: number, isCredits: boolean): string {
  return isCredits
    ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : value.toLocaleString();
}

/**
 * The absolute window a horizontal pixel range selects: every bucket the sweep touched, from the
 * one `left` falls in through the one `right` falls in. The returned end is exclusive, so it is
 * the start of the bucket after that last one — or, when the sweep ended on the final bucket, the
 * end of the window itself. A range that starts and ends in the same bucket selects that bucket.
 */
function rangeBetweenPixels(
  chart: ChartType,
  buckets: FilledActivityBucket[],
  period: ActivityPeriod,
  left: number,
  right: number,
): TimeRange {
  const scale = chart.scales.x;
  const last = buckets.length - 1;
  const clamp = (index: number) => Math.max(0, Math.min(last, index));
  // `CategoryScale.getValueForPixel` names the bucket a pixel falls in, so the bucket released on
  // is `after - 1`: reading it as the exclusive bound would drop it from every selection.
  const first = clamp(scale.getValueForPixel(left) ?? 0);
  const after = clamp(scale.getValueForPixel(right) ?? last) + 1;
  const end =
    after <= last
      ? buckets[after].periodStart
      : nextBucketStart(buckets[last].periodStart, period);
  return { start: buckets[first].periodStart, end };
}

function buildData(
  canvas: HTMLCanvasElement,
  {
    buckets,
    period,
    concurrencyLimit,
    drawn,
    clock,
  }: {
    buckets: FilledActivityBucket[];
    period: ActivityPeriod;
    concurrencyLimit: number | null;
    drawn: SeriesSpec[];
    clock: ClockPreference;
  },
) {
  const surface = computedColorOf(canvas, "--gray-1");

  // A zoomed-in window has few buckets; without markers one or two points draw nothing at all.
  const sparse = buckets.length <= 24;
  const line = {
    borderWidth: 2,
    borderJoinStyle: "round" as const,
    borderCapStyle: "round" as const,
    pointRadius: sparse ? 3 : 0,
    // A 24px target: the pointer only has to be near the value, not on it.
    pointHitRadius: 24,
    pointHoverRadius: 5,
    pointHoverBorderWidth: 2,
    pointHoverBorderColor: surface,
    tension: 0,
  };

  const datasets: ChartDataset<"bar" | "line", (number | null)[]>[] = drawn.map(
    (spec) => {
      const color = computedColorOf(canvas, spec.color);
      const data = buckets.map((bucket) =>
        spec.value(bucket, concurrencyLimit),
      );
      if (spec.kind === "bar") {
        return {
          type: "bar" as const,
          label: spec.label,
          data,
          backgroundColor: color,
          // Stated rather than left to Chart.js, which would derive a hover shade by parsing the
          // colour. `computedColorOf` returns whatever the browser computes a token to, and Chrome
          // keeps these in `oklch()`, which Chart.js's colour library cannot read; the shade it
          // derives from an unparseable colour is black, so a hovered bar turned solid black.
          // Canvas itself understands `oklch()`, so the colour is only a problem when something
          // has to do arithmetic on it.
          hoverBackgroundColor: color,
          borderWidth: 0,
        };
      }
      return {
        type: "line" as const,
        ...line,
        label: spec.label,
        data,
        borderColor: color,
        // The tooltip's swatch is drawn from this; left at the default it is a hollow box.
        backgroundColor: color,
        pointHoverBackgroundColor: color,
        // Same reason as the bars above: every hover colour is stated so Chart.js never derives
        // one from an `oklch()` string it cannot parse.
        hoverBorderColor: color,
        hoverBackgroundColor: color,
        ...(spec.dashed
          ? { borderDash: [4, 4], pointHitRadius: 0, pointHoverRadius: 0 }
          : {}),
      };
    },
  );

  const withDate = spansMultipleDays(buckets);

  return {
    labels: buckets.map((bucket) =>
      formatBucketStart(bucket.periodStart, period, { withDate, clock }),
    ),
    datasets,
  };
}
